<template>
  <ContextColorProvider context="wealth">
    <LoaderIndicator
      v-if="isLoading"
      class="profit-subscription__loader"
      with-container
    />
    <HelperViewStatus
      v-else-if="hasError"
      class="profit-subscription__error"
      title="Houve um problema ao carregar as informações."
      description="Aguarde um momento e tente novamente"
      status="alert"
      button-label="Tentar novamente"
      @click="getProfitInfo"
    />
    <div v-if="profitOption" class="profit-subscription">
      <HeroContainer v-show="step !== ProfitStep.REVIEW">
        <template #navbar="{ isNavbarTransparent }">
          <NavBar :transparent="isNavbarTransparent">
            <template #left>
              <BackButton
                data-testid="profit-main-back"
                @click="onBackButtonClick"
              />
            </template>
          </NavBar>
        </template>
        <template #hero>
          <Hero
            :image-small="profitHeroBg"
            :image-medium="profitHeroBg"
            :image-large="profitHeroBg"
            height="80vh"
            show-overlay
          >
            <Header
              v-if="getCurrentInfo"
              :profit-subscription-header="getCurrentInfo?.header"
              :profit-subscription-price="getCurrentInfo?.price"
            />
          </Hero>
        </template>
        <template #content>
          <ProfitSubscriptionContent
            v-if="!route.meta?.isPreview && profitOption"
            :profit-options="profitOption"
          />
          <ProfitPreviewContent
            v-else-if="profitPreview"
            :profit-preview="profitPreview"
            @subscribe="setStep(ProfitStep.TERMS)"
          />
        </template>
      </HeroContainer>
    </div>

    <ModalAcceptTerms
      v-if="profitPreview"
      :is-open="step === ProfitStep.TERMS"
      :terms="profitPreview?.terms"
      :is-loading="isLoadingSignature"
      @accept="acceptTerms"
      @cancel="setStep(null)"
    />
    <ProfitReview
      v-if="profitPreview"
      v-show="step === ProfitStep.REVIEW"
      :profit-info="profitPreview"
      :is-loading="isLoadingSubscription"
      @click="setStep(ProfitStep.PASSWORD)"
      @back="handleGoBackClick"
    />

    <VerifyPassword
      :is-open="step === ProfitStep.PASSWORD"
      :is-password-invalid="!isPasswordValid"
      :is-loading="isCheckingPassword"
      @close="setStep(null)"
      @confirm="checkPassword"
    />

    <ButtonContainer
      v-if="hasProfitPreviewButton || hasProfitOptionButton"
      class="profit-subscription__container-button"
    >
      <template #button-primary>
        <ButtonPrimary
          v-if="isProfitPreviewButtonBuy"
          :label="getBuyButtonLabel"
          size="regular"
          @click="setStep(ProfitStep.TERMS)"
        />
        <ButtonPrimary
          v-else
          :label="getAccessButtonLabel"
          size="regular"
          href="https://www.nelogica.com.br/conta"
          rel="noopener"
          target="_blank"
        />
      </template>
    </ButtonContainer>
  </ContextColorProvider>
</template>
<script setup lang="ts">
import {
  NavBar,
  ContextColorProvider,
  LoaderIndicator,
  ButtonContainer,
  ButtonPrimary,
  HelperViewStatus
} from '@warrenbrasil/nebraska-web';
import { Hero, HeroContainer } from '@/modules/common/components/Hero';
import { Header } from './components/Header';
import { BackButton } from '@/modules/common/components/buttons';
import { computed, onMounted, ref } from 'vue';
import { ProfitSubscriptionContent } from './components/ProfitSubscriptionContent';
import { ProfitPreviewContent } from './components/ProfitPreviewContent';
import { ModalAcceptTerms } from './components/ModalAcceptTerms';
import { ProfitReview } from './components/ProfitReview';
import { VerifyPassword } from './components/VerifyPassword';
import {
  getProfitOptions,
  getProfitPreview,
  signProfitOption,
  verifyPassword
} from '@/modules/trade/services/profit';
import type {
  ProfitOptions,
  ProfitPreview
} from '@/modules/trade/services/profit';
import { useRoute, useRouter } from 'vue-router/composables';
import { getExternalAssetPath } from '@/modules/common/helpers/assets-resolver';
import { signature } from '@/modules/trade/services/terms';
import type { SignatureTerms } from '@/modules/trade/services/terms';
import { notify } from '@/modules/common/helpers/notification';
import { NotificationTypes } from '@/modules/common/services/http/notification';
import { isBFFError } from '@/modules/common/helpers/bff';

const isLoading = ref(false);
const hasError = ref(false);
const profitPreview = ref<ProfitPreview>();
const profitOption = ref<ProfitOptions>();
const profitOptionDetail = ref<ProfitOptions['profits']['options'][0] | null>(
  null
);

enum ProfitStep {
  TERMS = 'terms',
  REVIEW = 'review',
  PASSWORD = 'password'
}

const step = ref<ProfitStep | null>();
const signatureTerms = ref<SignatureTerms | null>();
const isLoadingSignature = ref(false);
const isLoadingSubscription = ref(false);
const isPasswordValid = ref(true);
const isCheckingPassword = ref(false);

const route = useRoute();
const router = useRouter();

const profitHeroBg = getExternalAssetPath('hero/profit/desktop.jpg');

function handleGoBackClick() {
  router.back();
}

onMounted(() => {
  getProfitInfo();
});

const hasProfitPreviewButton = computed(() => {
  return profitPreview.value?.button?.active;
});

const hasProfitOptionButton = computed(() => {
  return profitOption.value?.button;
});

const isProfitPreviewButtonBuy = computed(() => {
  return profitPreview.value?.button?.action === 'buy';
});

const getBuyButtonLabel = computed(() => {
  if (hasProfitPreviewButton && isProfitPreviewButtonBuy)
    return profitPreview.value?.button?.text;
  return 'Assinar Profit';
});

const getAccessButtonLabel = computed(() => {
  return profitOption.value?.button?.text || 'Acessar Profit';
});

async function getProfitInfo() {
  isLoading.value = true;
  hasError.value = false;

  try {
    if (route.meta?.isPreview) {
      const profitValue = Number(route.params.profit);

      profitOptionDetail.value = (
        await getProfitOptions()
      ).profits.options.filter(option => option.value === profitValue)[0];

      profitPreview.value = await getProfitPreview(profitValue);
      profitOption.value = await getProfitOptions();
    } else {
      profitOption.value = await getProfitOptions();
    }
  } catch (error) {
    hasError.value = true;
  } finally {
    isLoading.value = false;
  }
}

const getCurrentInfo = computed(() => {
  return route.meta!.isPreview ? profitPreview.value : profitOption.value;
});

function setStep(targetStep: ProfitStep | null) {
  step.value = targetStep;
}

async function acceptTerms() {
  isLoadingSignature.value = true;

  try {
    const { subscriptionPlan, value } = profitOptionDetail.value!;
    signatureTerms.value = await signature(`${subscriptionPlan}-${value}`);
    setStep(ProfitStep.REVIEW);
  } catch (e) {
    getErrorMessage(e);
    setStep(null);
  } finally {
    isLoadingSignature.value = false;
  }
}

async function checkPassword(inputPassword: string) {
  isCheckingPassword.value = true;
  try {
    isPasswordValid.value = await verifyPassword(inputPassword);

    if (isPasswordValid.value) subscribePlan();
  } catch (e) {
    getErrorMessage(e);
  } finally {
    isCheckingPassword.value = false;
  }
}

async function subscribePlan() {
  isLoadingSubscription.value = true;

  try {
    const { subscriptionPlan, value } = profitOptionDetail.value!;

    const subscription = await signProfitOption({
      product: value,
      subscriptionPlan,
      acceptanceTerm: signatureTerms.value?.term ?? ''
    });

    notify({
      group: 'wrn-notifications',
      text: subscription.message,
      type: NotificationTypes.success
    });

    router.push({ name: 'profitSubscription' });
  } catch (e) {
    getErrorMessage(e);
  } finally {
    isLoadingSubscription.value = false;
    setStep(null);
  }
}

function getErrorMessage(e: unknown) {
  let errorMessage = 'Houve um problema ao assinar o Profit.';
  if (isBFFError(e)) {
    errorMessage = e.error.message.formatted;
  }
  notify({
    group: 'wrn-notifications',
    text: errorMessage,
    type: NotificationTypes.error
  });
}

function onBackButtonClick() {
  if (route.meta?.isPreview) {
    router.push({ name: 'profitSubscription' });
  } else {
    router.push({ name: 'trade' });
  }
}
</script>
<style scoped lang="less">
:deep(.template-container__content) {
  z-index: unset;
}
:deep(.hero__content) {
  margin: 0 auto @size-spacing-x450;
}

.profit-subscription {
  &__loader {
    height: 100vh;
  }

  &__error {
    height: 100vh;
    margin: auto;
    justify-content: center;
  }

  &__container-button {
    position: fixed;
    bottom: 0;
    z-index: 1;
    background: @background-secondary;
  }
}
</style>
